import React from 'react';

import ListControls from '@sharedCR/common/ListControls';
import _ from 'lodash';
import { compose } from 'redux';

import { FieldType } from 'constants/formGenerator';
import { MIN_ALTERNATE_NAMES_NUMBER } from 'constants/loanApplication';
import { suffixOptions } from 'constants/options';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';

import { alternateNameGroupStyles } from './AlternateName.styles';
import {
  getCorrectedNameInModelByIndex,
  getRequiredAlternateNameFieldSchema,
} from './AlternateName.utils';

export const getAlternateNameConfigFields = ({ entityId }) => ({
  firstName: {
    fieldId: 'firstName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].alternateNames[0].firstName`,
    inputProps: {
      placeholder: '-',
      label: 'First Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.ALTERNATE_NAME_FORM_ALTERNATE_FIRST_NAME_INPUT,
      'data-test-value': 'alternate-first-name',
    },
  },
  middleName: {
    fieldId: 'middleName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].alternateNames[0].middleName`,
    inputProps: {
      placeholder: '-',
      label: 'Middle Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.ALTERNATE_NAME_FORM_ALTERNATE_MIDDLE_NAME_INPUT,
      'data-test-value': 'alternate-middle-name',
    },
  },
  lastName: {
    fieldId: 'lastName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].alternateNames[0].lastName`,
    inputProps: {
      placeholder: '-',
      label: 'Last Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.ALTERNATE_NAME_FORM_ALTERNATE_LAST_NAME_INPUT,
      'data-test-value': 'alternate-last-name',
    },
  },
  suffix: {
    fieldId: 'suffix',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].alternateNames[0].suffix`,
    inputProps: {
      placeholder: '-',
      label: 'Suffix',
      disabled: false,
      required: false,
      options: suffixOptions,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.ALTERNATE_NAME_FORM_ALTERNATE_SUFFIX_SELECT,
      'data-test-value': suffixOptions[0].value,
    },
  },
});

export const getAlternateNameConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    section,
    onAlternateNameDelete = _.noop,
    onAlternateNameAdd,
    onAlternateNameFieldChange,
    alternateNamesCount,
  }) => {
    const alternateNameConfigFields = getAlternateNameConfigFields({ entityId });

    const isAlternateNameDeleteAllowed = alternateNamesCount > MIN_ALTERNATE_NAMES_NUMBER;

    const getCustomControls = index => (
      <ListControls
        isRemoveVisible={isAlternateNameDeleteAllowed}
        onAdd={onAlternateNameAdd}
        onRemove={onAlternateNameDelete?.(index)}
      />
    );

    return {
      groups: [
        {
          type: FieldType.GROUP,
          key: `${entityId}-${section}`,
          fields: Array.from({ length: alternateNamesCount || 1 }).map((_, index) => {
            const firstNameNameInModel = getCorrectedNameInModelByIndex(
              alternateNameConfigFields.firstName.nameInModel,
              index,
            );
            const lastNameNameInModel = getCorrectedNameInModelByIndex(
              alternateNameConfigFields.lastName.nameInModel,
              index,
            );
            const requiredFieldsNamesInModel = [firstNameNameInModel, lastNameNameInModel];

            return {
              type: FieldType.GROUP,
              customStyles: alternateNameGroupStyles,
              customControls: getCustomControls(index),
              key: `${index}`,
              fields: [
                {
                  ...alternateNameConfigFields.firstName,
                  nameInModel: firstNameNameInModel,
                  inputProps: {
                    ...alternateNameConfigFields.firstName.inputProps,
                    customOnChange: (...props) =>
                      onAlternateNameFieldChange(props, [lastNameNameInModel]),
                    customSchema: () => getRequiredAlternateNameFieldSchema('firstName'),
                  },
                },
                {
                  ...alternateNameConfigFields.middleName,
                  nameInModel: getCorrectedNameInModelByIndex(
                    alternateNameConfigFields.middleName.nameInModel,
                    index,
                  ),
                  inputProps: {
                    ...alternateNameConfigFields.middleName.inputProps,
                    customOnChange: (...props) =>
                      onAlternateNameFieldChange(props, requiredFieldsNamesInModel),
                  },
                },
                {
                  ...alternateNameConfigFields.lastName,
                  nameInModel: lastNameNameInModel,
                  inputProps: {
                    ...alternateNameConfigFields.lastName.inputProps,
                    customOnChange: (...props) =>
                      onAlternateNameFieldChange(props, [firstNameNameInModel]),
                    customSchema: () => getRequiredAlternateNameFieldSchema('lastName'),
                  },
                },
                {
                  ...alternateNameConfigFields.suffix,
                  nameInModel: getCorrectedNameInModelByIndex(
                    alternateNameConfigFields.suffix.nameInModel,
                    index,
                  ),
                  inputProps: {
                    ...alternateNameConfigFields.suffix.inputProps,
                    customOnChange: (...props) =>
                      onAlternateNameFieldChange(props, requiredFieldsNamesInModel, true),
                  },
                },
              ],
            };
          }),
        },
      ],
    };
  },
);
